import { useRef } from 'react';
import styled from '@emotion/styled';
import { MenuItem } from './MenuItem';
import { Jargon } from 'components/jargon/Jargon';

import { useLocation } from 'react-router-dom';
import { delay } from 'lodash';
import { t } from '@lingui/macro';
import DelogueLogo from 'img/delogue-logo.svg';
import { colors } from 'styles/theme/colors';
import { Box } from '@mui/material';
import { navigationItems } from 'modules/accessControl/config/navigation';
import { useNavItemAccess } from 'modules/accessControl/hooks/useNavItemAccess';
import type { NavItem } from 'modules/accessControl/types/navigation';
import useLocalStorageState from 'use-local-storage-state';
import { ExpandableMenuItem } from './ExpandableMenuItem';
import { Image } from 'components/image/Image';
import { Tooltip } from 'components/tooltip/Tooltip';
import { Button } from 'components/button/Button';

export type SideBarOpen = 'closed' | 'open' | 'fixed';

type SidebarProps = {
  children: React.ReactNode[];
};

export const Page = ({
  item,
  onMenuClick,
  isSideBarOpen,
  marginLeft,
  marginRight,
}: {
  item: NavItem;
  onMenuClick: () => void;
  isSideBarOpen: SideBarOpen;
  marginLeft?: number;
  marginRight?: number;
}) => {
  const location = useLocation();
  const hasAccess = useNavItemAccess(item);
  if (!hasAccess) return null;

  return (
    <MenuItem
      url={item.url}
      MenuIcon={item.MenuIcon}
      topPadding={item.topPadding}
      selected={
        (item.url.startsWith(location.pathname) || location.pathname.startsWith(item.url)) &&
        location.pathname.length > 1
      }
      onClick={onMenuClick}
      extend={isSideBarOpen !== 'closed'}
      marginLeft={marginLeft}
      marginRight={marginRight}
    >
      <Jargon text={item.text} />
    </MenuItem>
  );
};

export const Sidebar = ({ children }: SidebarProps) => {
  const [isSideBarOpen, setIsSideBarOpen] = useLocalStorageState<SideBarOpen>('isSideBarOpen', {
    defaultValue: 'closed',
  });
  const timer = useRef<NodeJS.Timeout | null>(null);

  const onEnter = () => {
    timer.current = setTimeout(() => {
      if (isSideBarOpen === 'closed') {
        setIsSideBarOpen('open');
      }
    }, 1000);
  };

  const onLeave = () => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    delay(() => {
      if (isSideBarOpen === 'open') {
        setIsSideBarOpen('closed');
      }
    }, 200);
  };

  const onKeep = () => {
    if (isSideBarOpen === 'open' || isSideBarOpen === 'closed') {
      setIsSideBarOpen('fixed');
      return;
    }
    if (isSideBarOpen === 'fixed') {
      setIsSideBarOpen('open');
      return;
    }
  };

  const onMenuClick = () => {
    if (isSideBarOpen === 'open') {
      setIsSideBarOpen('closed');
    }
  };

  return (
    <Container>
      <Menu onMouseEnter={onEnter} onMouseLeave={onLeave} open={isSideBarOpen}>
        <LogoAndMenu>
          <LogoCon>
            <Image src={DelogueLogo} alt="Delogue logo" width={40} />
          </LogoCon>
          <MenuItemsContainer>
            {navigationItems
              .filter(item => item.pin !== 'bottom')
              .map(item => {
                const key = `${item.url}-${item.text}`;
                if (item.subItems) {
                  return (
                    <ExpandableMenuItem
                      key={key}
                      item={item}
                      onMenuClick={onMenuClick}
                      isSideBarOpen={isSideBarOpen}
                    />
                  );
                }
                return (
                  <Page
                    key={key}
                    item={item}
                    onMenuClick={onMenuClick}
                    isSideBarOpen={isSideBarOpen}
                  />
                );
              })}
          </MenuItemsContainer>
        </LogoAndMenu>
        <FooterNav>
          {navigationItems
            .filter(item => item.pin === 'bottom')
            .map(item => (
              <Page
                key={`${item.url}-${item.text}`}
                item={item}
                onMenuClick={onMenuClick}
                isSideBarOpen={isSideBarOpen}
              />
            ))}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              justifyContent: isSideBarOpen !== 'closed' ? 'flex-end' : 'center',
            }}
          >
            <Tooltip
              title={isSideBarOpen === 'fixed' ? t`Collapse sidebar` : t`Lock navigation bar`}
            >
              <Button variant="link" onClick={() => onKeep()}>
                <SideBarSvg isFixed={isSideBarOpen === 'fixed'} />
              </Button>
            </Tooltip>
          </Box>
        </FooterNav>
      </Menu>
      <TopAndContent fixed={isSideBarOpen === 'fixed'}>
        <Box
          sx={{
            zIndex: 0,
            minHeight: '100vh',
          }}
        >
          {children[0]}
          {children[1]}
        </Box>
      </TopAndContent>
    </Container>
  );
};

const TopAndContent = styled.div<{ fixed: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  margin-left: ${p => (p.fixed ? 312 : 88)}px;
  position: relative;
  overflow-x: auto;
`;

const LogoAndMenu = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 32px;
  width: 100%;
`;

const LogoCon = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  position: relative;
  main {
    z-index: 1;
  }
`;

const MenuItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FooterNav = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  margin-top: auto;
`;

const Menu = styled.nav<{ open: 'closed' | 'open' | 'fixed' }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  background-color: ${p => p.theme.palette.bg.primary};
  height: 100%;
  width: ${p => (p.open !== 'closed' ? '312px' : '88px')};
  transition: width 0.2s ease-in-out;
  justify-content: space-between;
  align-items: flex-end;
  padding: 24px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  z-index: 2;
  overflow-x: hidden;
  overflow-y: auto;
`;

const SideBarSvg = ({ isFixed }: { isFixed: boolean }) => {
  const pathProps = {
    ...(isFixed ? { fill: colors.brand[400] } : { fill: colors.brand[100] }),
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 256 256">
      <path d="M88,48V208H40a8,8,0,0,1-8-8V56a8,8,0,0,1,8-8Z" {...pathProps} />
      <path
        d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40ZM40,56H80V200H40ZM216,200H96V56H216V200Z"
        fill={colors.grey[700]}
      />
    </svg>
  );
};
